import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Button, Press, cx, haptic } from '@dorixona/ui'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { LoginCta } from '../../ui/LoginCta'
import { Sheet } from '../../ui/Sheet'
import { Failed, Loading, failureText } from '../../ui/Load'
import { useSession } from '../../app/session'
import { Field } from '../auth/ui/Field'
import { AddressFinder } from '../checkout/AddressFinder'
import { useAddressDraft } from '../checkout/addressDraft'
import { chooseAddress, useChosenAddress } from '../checkout/chosenAddress'
import {
  useAddresses,
  useDeleteAddress,
  useSaveAddress,
  useUpdateAddress,
  type Address,
} from './api'

/** Куда везти: улица и точка (у старого адреса точки может не быть). */
interface AddressTarget {
  label: string
  lat: number | null
  lng: number | null
}

/** Ярлык — кнопка на оформлении, длиннее сервер не примет (F-33). */
const MAX_TAG = 32

/**
 * Адрес из «Моих адресов»: `/profile/addresses/new` — новый,
 * `/profile/addresses/:addressId` — правка сохранённого. Форма ждёт список:
 * адрес на правку берём из него, отдельной ручки на один адрес нет.
 */
export function AddressEditScreen() {
  const navigate = useNavigate()
  const { addressId = 'new' } = useParams()
  const { t } = useTranslation(['account', 'common'])
  const { isAuthenticated } = useSession()
  const addresses = useAddresses(isAuthenticated)
  const id = addressId === 'new' ? 0 : Number(addressId)
  const list = addresses.data ?? []
  const address = id ? list.find((item) => item.id === id) : undefined
  const title = id ? t('account:addresses.editTitle') : t('account:addresses.newTitle')

  return (
    <section className="screen checkout-wrap addr-wrap">
      <div className="mhead">
        <BackButton to="/profile/addresses" />
        <h1>{title}</h1>
      </div>

      {!isAuthenticated ? (
        <LoginCta
          onSignIn={() => {
            void navigate('/login')
          }}
        />
      ) : addresses.isPending ? (
        <Loading what={t('common:state.loading')} shape="page" />
      ) : addresses.isError ? (
        <Failed
          error={addresses.error}
          onRetry={() => {
            void addresses.refetch()
          }}
        />
      ) : id && !address ? (
        <div className="card empty">
          <Icon name="i-pin" size={54} />
          <div className="t">{t('account:addresses.notFound')}</div>
        </div>
      ) : (
        <AddressForm
          key={address?.id ?? 'new'}
          address={address ?? null}
          others={list.filter((item) => item.id !== id)}
        />
      )}
    </section>
  )
}

interface AddressFormProps {
  /** null — новый адрес. */
  address: Address | null
  /** Остальные адреса: улица у покупателя одна на адрес. */
  others: Address[]
}

function AddressForm({ address, others }: AddressFormProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['account', 'common'])
  const draft = useAddressDraft(
    address && address.lat !== null && address.lng !== null
      ? { label: address.label, lat: address.lat, lng: address.lng }
      : null,
  )
  const save = useSaveAddress()
  const update = useUpdateAddress()
  const chosen = useChosenAddress()
  const [tag, setTag] = useState(address?.tag ?? '')
  const [makeDefault, setMakeDefault] = useState(address?.def ?? false)
  const [message, setMessage] = useState('')
  const [deleting, setDeleting] = useState(false)

  // Точку не трогали — у сохранённого адреса остаются прежние улица и точка.
  const target =
    draft.selected ??
    (address && !draft.spot ? { label: address.label, lat: address.lat, lng: address.lng } : null)
  const pending = save.isPending || update.isPending
  const presets = [t('account:addresses.tags.home'), t('account:addresses.tags.work')]

  function leave() {
    // Оформление держит выбранный адрес у себя: устаревший сбрасываем.
    if (address && chosen?.id === address.id) chooseAddress(null)
    if (location.key === 'default') void navigate('/profile/addresses', { replace: true })
    else void navigate(-1)
  }

  function submit(target: AddressTarget) {
    // Сервер хранит улицу один раз: правка в чужую улицу упала бы, новый
    // адрес молча слился бы с прежним.
    if (others.some((item) => item.label === target.label.trim())) {
      setMessage(t('account:addresses.duplicate'))
      return
    }
    setMessage('')
    const input = { tag, ...target, makeDefault: makeDefault && !address?.def }
    const done = {
      onSuccess: () => {
        haptic('success')
        leave()
      },
      onError: (error: unknown) => {
        haptic('error')
        setMessage(failureText(error, t))
      },
    }
    if (address) update.mutate({ id: address.id, ...input }, done)
    else save.mutate(input, done)
  }

  return (
    <>
      <AddressFinder draft={draft} hint={address?.label} />

      <div className="card addr-form">
        <Field
          label={t('account:addresses.tag')}
          optional={t('account:edit.optional')}
          hint={t('account:addresses.tagHint')}
        >
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              maxLength={MAX_TAG}
              placeholder={t('account:addresses.tagPlaceholder')}
              value={tag}
              onChange={(event) => {
                setTag(event.target.value)
              }}
            />
          )}
        </Field>
        <div className="addr-presets" role="group" aria-label={t('account:addresses.presets')}>
          {presets.map((name) => (
            <Press
              key={name}
              squish="chip"
              className={cx('chip', tag === name && 'on')}
              aria-pressed={tag === name}
              onPress={() => {
                setTag(tag === name ? '' : name)
              }}
            >
              {name}
            </Press>
          ))}
        </div>
      </div>

      <div className="acc-sec addr-default">
        <label className="acc-toggle">
          <div>
            <div className="t1">{t('account:addresses.makeDefault')}</div>
            <div className="t2">
              {address?.def
                ? t('account:addresses.isDefaultHint')
                : t('account:addresses.makeDefaultHint')}
            </div>
          </div>
          <span className="switch">
            <input
              type="checkbox"
              role="switch"
              aria-label={t('account:addresses.makeDefault')}
              checked={makeDefault}
              // Основной снимают, отметив другой: сервер умеет только «сделать основным».
              disabled={address?.def}
              onChange={(event) => {
                setMakeDefault(event.target.checked)
              }}
            />
            <span className="tr2" />
            <span className="th" />
          </span>
        </label>
      </div>

      <div className="addr-confirm">
        <Button
          disabled={!target || pending}
          onPress={() => {
            // Кнопка заперта, пока адреса нет: сюда попадаем только с ним.
            submit(target as AddressTarget)
          }}
        >
          {pending ? t('account:edit.saving') : t('common:actions.save')}
        </Button>
        {message ? (
          <p className="auth-note" role="status">
            {message}
          </p>
        ) : null}
        {address ? (
          <>
            <Button
              className="btn-danger-soft"
              aria-haspopup="dialog"
              onPress={() => {
                setDeleting(true)
              }}
            >
              {t('account:addresses.remove')}
            </Button>
            <RemoveAddressSheet
              address={address}
              opened={deleting}
              onClose={() => {
                setDeleting(false)
              }}
              onRemoved={leave}
            />
          </>
        ) : null}
      </div>
    </>
  )
}

interface RemoveAddressSheetProps {
  address: Address
  opened: boolean
  onClose: () => void
  onRemoved: () => void
}

/** Подтверждение: адрес пропадёт и из кабинета, и из выбора на оформлении. */
function RemoveAddressSheet({ address, opened, onClose, onRemoved }: RemoveAddressSheetProps) {
  const { t } = useTranslation(['account', 'common'])
  const remove = useDeleteAddress()

  const close = () => {
    remove.reset()
    onClose()
  }

  return (
    <Sheet opened={opened} label={t('account:addresses.confirmTitle')} onClose={close}>
      <div className="fsheet">
        <div className="fhead">
          <h2>{t('account:addresses.confirmTitle')}</h2>
          <Press
            squish="icon"
            className="fclose"
            aria-label={t('common:actions.close')}
            onPress={close}
          >
            <Icon name="i-x" size={18} />
          </Press>
        </div>
        <p className="cardform-lead">
          {t('account:addresses.confirmLead', { address: address.tag || address.label })}
        </p>
        {remove.isError ? (
          <p className="acc-note acc-note-err" role="alert">
            {failureText(remove.error, t)}
          </p>
        ) : null}
        <div className="ffoot">
          <Button variant="ghost" onPress={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            className="btn-danger"
            disabled={remove.isPending}
            onPress={() => {
              remove.mutate(address.id, {
                onSuccess: () => {
                  haptic('success')
                  onClose()
                  onRemoved()
                },
                onError: () => {
                  haptic('error')
                },
              })
            }}
          >
            {remove.isPending
              ? t('account:addresses.removing')
              : t('account:addresses.removeShort')}
          </Button>
        </div>
      </div>
    </Sheet>
  )
}
